import { useQuery } from '@tanstack/react-query';
import { useLayoutEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { getAutoReplyMessageAssets } from 'api/autoReply';
import { getScenario } from 'api/scenarios';
import type { ScenarioStepV1 } from 'api/scenarios';
import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { pageCardClasses } from 'components/AppLayout/styles';
import { queryKeys } from 'constants/queryKeys';
import { routes } from 'constants/routes';
import { useOrgPath } from 'hooks/useOrgPath';
import { useCurrentUser, useTags } from 'hooks';
import { EOrganizationFeature } from 'types/enum';
import {
  canFetchOrganizationFeature,
  isOrganizationFeatureEnabled,
} from 'utils/organizationFeature';
import { issueMessage } from 'utils/scenarioIssues';

import { isTimingBehindPrevious, timingSummary } from '../duration';
import { conditionCaseLabel, reachTagLabel } from '../labels';
import { MessagePreview } from '../MessagePreview';
import { bandHeight, buildJumps, depthsOf, elbowPath, jumpPath, LANE_HEIGHT, MARKER_WIDTH, NODE_GAP, NODE_HEIGHT, NODE_WIDTH, nodeCenterX, nodeX, nodeY, ROW_HEIGHT } from './flow';

const chipClass = 'rounded-full border border-border bg-white px-2 py-0.5 text-[11px] font-semibold shadow-sm';
/** 20ステップ級でも「全体表示」が頭打ちにならない下限にする。 */
const MIN_SCALE = 0.1;
const MAX_SCALE = 1.5;
const SCALE_STEP = 0.2;
const clampScale = (value: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number(value.toFixed(2))));

/** 全ステップを左から右へのフロー図として描き、分岐は線でつなぐ読み取り専用の画面。 */
export default function ScenarioPreview() {
  const { scenarioId } = useParams();
  const orgPathFor = useOrgPath();
  const id = Number(scenarioId);
  // queryを止めるだけだとisPendingのまま「読み込み中」で固まるので、IDの妥当性は描画側でも分岐する
  const validId = Number.isSafeInteger(id) && id > 0;
  const { data: currentUser } = useCurrentUser();
  // タグ管理OFFではタグ一覧を取得せず、取得エラーではなく表示できない理由を出す
  const canUseTags = isOrganizationFeatureEnabled(currentUser, EOrganizationFeature.TAG_MANAGEMENT);
  const { tags, error: tagsError } = useTags({ enabled: canFetchOrganizationFeature(currentUser, EOrganizationFeature.TAG_MANAGEMENT) });
  const detail = useQuery({
    queryKey: ['scenarios', 'preview', id],
    queryFn: () => getScenario(id),
    enabled: validId,
    retry: false,
  });
  const assets = useQuery({
    queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS],
    queryFn: () => getAutoReplyMessageAssets(),
    retry: false,
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [content, setContent] = useState({ width: 0, height: 0 });
  const [selected, setSelected] = useState(0);

  const scenario = detail.data?.detail;
  const steps: ScenarioStepV1[] = scenario?.definition.steps ?? [];
  const usesTags = scenario?.definition.trigger?.type === 'tag_added' || steps.some((step) => step.condition !== null || step.onReachTagId !== null);
  const jumps = buildJumps(steps);
  const band = bandHeight(jumps);
  const depths = depthsOf(steps, jumps);
  const rows = depths.length ? Math.max(...depths) + 1 : 1;
  const width = steps.length ? nodeX(steps.length - 1) + NODE_WIDTH : 0;
  const height = band + rows * ROW_HEIGHT;
  const centerY = (index: number) => nodeY(depths[index], band) + NODE_HEIGHT / 2;
  const editPath = orgPathFor(routes.SCENARIO_EDIT.replace(':scenarioId', String(id)));

  // transformは配置に影響しないので、素の大きさを測って枠の寸法を決める
  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element) return undefined;
    const measure = () => setContent({ width: element.scrollWidth, height: element.scrollHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [steps.length]);

  if (!validId) return <><Alert>シナリオIDが正しくありません。</Alert></>;

  return (
    <>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="grid gap-3">
          <Link className="justify-self-start text-sm font-semibold text-accent underline" to={editPath}>
            ← 編集画面に戻る
          </Link>
          <div>
            <h1 className="text-2xl font-display text-ink">一括プレビュー</h1>
            <p className="mt-2 text-sm text-muted">
              {scenario ? `${scenario.name}の全ステップを配信順に並べています。` : 'シナリオの全ステップを配信順に並べています。'}この画面では編集できません。
            </p>
          </div>
        </header>

        {detail.error && <div className="mt-6"><Alert>{issueMessage(detail.error, 'シナリオを取得できませんでした。')}</Alert></div>}
        {/* タグ・素材が取れないと「不明なタグ」等へfallbackするため、通信障害と削除を取り違えないよう明示する。
            取得済みの一覧が残っている場合は表示自体は成立するので、古い可能性がある旨だけを伝える */}
        {!canUseTags && usesTags && <div className="mt-3"><Alert>タグ管理は現在ご利用いただけないため、タグ名を表示できません。条件は「不明なタグ」と出ます。</Alert></div>}
        {canUseTags && tagsError && <div className="mt-3"><Alert>{`${tagsError}（${tags.length ? '表示中のタグ名は最新でない可能性があります' : 'タグ名を表示できないため、条件は「不明なタグ」と出ます'}）`}</Alert></div>}
        {assets.error && <div className="mt-3"><Alert>{issueMessage(assets.error, '素材を取得できませんでした。')}（{assets.data?.length ? '表示中の素材名は最新でない可能性があります' : '素材名を表示できません'}）</Alert></div>}

        <div className="mt-6">
          {detail.isPending ? (
            <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted">読み込み中...</p>
          ) : steps.length ? (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" variant="outline" aria-label="縮小" disabled={scale <= MIN_SCALE} onClick={() => setScale((current) => clampScale(current - SCALE_STEP))}>−</Button>
                <Button type="button" size="sm" variant="outline" aria-label="拡大" disabled={scale >= MAX_SCALE} onClick={() => setScale((current) => clampScale(current + SCALE_STEP))}>＋</Button>
                <Button type="button" size="sm" variant="outline" disabled={scale === 1} onClick={() => setScale(1)}>等倍</Button>
                <span className="text-xs text-muted">{Math.round(scale * 100)}%</span>
              </div>
              <section aria-label="配信フロー図" className="overflow-auto rounded-2xl bg-[radial-gradient(circle,rgba(15,23,42,0.08)_1px,transparent_1px)] [background-size:16px_16px] p-5">
                {/* 拡大縮小してもスクロール量が合うよう、外枠に縮尺後の大きさを持たせる */}
                <div style={content.width ? { width: content.width * scale, height: content.height * scale } : undefined}>
                  <div ref={contentRef} className="flex w-max items-start gap-0" style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                <div className="shrink-0" style={{ paddingTop: band, width: MARKER_WIDTH }}>
                  <div className="flex flex-col items-center justify-center gap-1 text-center text-[11px] font-semibold text-muted" style={{ height: NODE_HEIGHT }}>
                    <span aria-hidden className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-accent bg-white text-accent">▶</span>
                    登録された時点から開始
                  </div>
                </div>

                <div className="relative shrink-0" style={{ width, height }}>
                  {/* 条件を満たしたときだけ通る区間を下の段へ降ろし、分岐を枝として見せる */}
                  <svg className="pointer-events-none absolute left-0 top-0" width={width} height={height} aria-hidden focusable="false">
                    <defs>
                      <marker id="flow-arrow" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
                        <path d="M0,0 L9,4.5 L0,9 z" className="fill-neutral-400" />
                      </marker>
                    </defs>
                    {steps.slice(0, -1).map((step, index) => (
                      <path
                        key={`edge-${step.id}`}
                        d={elbowPath(nodeX(index) + NODE_WIDTH, centerY(index), nodeX(index + 1) - 10, centerY(index + 1))}
                        className="stroke-neutral-400"
                        strokeWidth={2}
                        fill="none"
                        markerEnd="url(#flow-arrow)"
                      />
                    ))}
                    {jumps.map((jump) => (
                      <path
                        key={`jump-${jump.from}-${jump.to}`}
                        d={jumpPath(jump, band, nodeY(depths[jump.from], band), nodeY(depths[jump.to], band))}
                        className="stroke-neutral-400"
                        strokeWidth={2}
                        fill="none"
                        strokeDasharray="5 4"
                        markerEnd="url(#flow-arrow)"
                      />
                    ))}
                  </svg>

                  {/* 線が二手に分かれるときだけ、本線側にも「満たす」を置いて対にする */}
                  {jumps.map((jump) => (
                    <span
                      key={`true-label-${jump.from}`}
                      className={`absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-green-800 ${chipClass}`}
                      style={{ left: nodeX(jump.from) + NODE_WIDTH + NODE_GAP / 2, top: (centerY(jump.from) + centerY(jump.from + 1)) / 2 }}
                    >
                      {conditionCaseLabel(steps[jump.from].condition!, tags, true)}
                    </span>
                  ))}

                  {/* 飛び先が画面外でも行き先が読めるよう、札は飛び元の側へ寄せて置く */}
                  {jumps.map((jump) => (
                    <span
                      key={`jump-label-${jump.from}-${jump.to}`}
                      className={`absolute -translate-y-1/2 whitespace-nowrap text-muted ${chipClass}`}
                      style={{ left: nodeCenterX(jump.from) + 14, top: band - (jump.lane + 1) * LANE_HEIGHT }}
                    >
                      <span>{conditionCaseLabel(steps[jump.from].condition!, tags, false)}</span>
                      <span aria-hidden className="mx-1 text-accent">→</span>
                      {/* 札に行き先を書いてあるので読み上げも成立する。
                          飛ばす範囲まで必要なら、そのステップを選べば下の説明で読める */}
                      <span>ステップ{jump.to + 1}へ</span>
                    </span>
                  ))}

                  {steps.map((step, index) => {
                    const incoming = steps.flatMap((other, from) => (other.condition && other.falseTargetStepId === step.id ? [from + 1] : []));
                    const active = selected === index;
                    return (
                      <div key={step.id} className="absolute" style={{ left: nodeX(index), top: nodeY(depths[index], band), width: NODE_WIDTH }}>
                        <button
                          type="button"
                          aria-pressed={active}
                          onClick={() => setSelected(index)}
                          style={{ height: NODE_HEIGHT }}
                          className={`grid w-full content-center gap-0.5 rounded-2xl border-2 px-4 text-left transition ${active ? 'border-accent bg-accent-soft shadow-md' : 'border-border bg-white hover:border-accent/60'}`}
                        >
                          <span className="text-[11px] font-semibold text-accent">ステップ {index + 1}</span>
                          <span className="text-sm font-semibold text-ink">{timingSummary(step.timing, index)}</span>
                        </button>

                        {/* 飛び先を指定していない条件は線が分かれないので、ノード側で振る舞いを書く */}
                        {step.condition && !step.falseTargetStepId && (
                          <span className={`absolute -top-3 left-4 whitespace-nowrap text-muted ${chipClass}`}>
                            {conditionCaseLabel(step.condition, tags, false)}は送らず{index + 1 < steps.length ? '次へ' : '終了'}
                          </span>
                        )}
                        {incoming.length > 0 && (
                          <span className={`absolute -bottom-3 left-4 whitespace-nowrap text-muted ${chipClass}`}>ステップ{incoming.join('・')}から合流</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="shrink-0 pl-6" style={{ paddingTop: band, width: MARKER_WIDTH + NODE_GAP }}>
                  <div className="flex flex-col items-center justify-center gap-1 text-center text-[11px] font-semibold text-muted" style={{ height: NODE_HEIGHT }}>
                    <span aria-hidden className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-border bg-white text-muted">■</span>
                    最後まで送ると完了
                  </div>
                </div>
                  </div>
                </div>
              </section>

              {/* 図は構造だけを見せ、中身は選んだステップの分だけ下に出す */}
              {(() => {
                const step = steps[Math.min(selected, steps.length - 1)];
                const index = Math.min(selected, steps.length - 1);
                if (!step) return null;
                const target = step.falseTargetStepId ? steps.findIndex(({ id }) => id === step.falseTargetStepId) : -1;
                const incoming = steps.flatMap((other, from) => (other.condition && other.falseTargetStepId === step.id ? [from + 1] : []));
                const falseLabel =
                  target >= 0
                    ? jumps.find((jump) => jump.from === index)?.description
                    : index + 1 < steps.length
                      ? `ステップ${index + 2}へ進みます（ステップ${index + 1}は送りません）`
                      : 'ここで配信が終わります';
                return (
                  <div className="mt-4 grid gap-3 rounded-2xl border border-border/70 bg-white p-4">
                    <div className="flex flex-wrap items-baseline gap-3">
                      <h2 className="text-lg font-semibold text-ink">ステップ {index + 1} の内容</h2>
                      <p className="text-sm font-semibold text-primary-800">{timingSummary(step.timing, index)}</p>
                      <p className="text-xs text-muted">図のステップを選ぶと、ここの表示が切り替わります</p>
                    </div>
                    {/* 配信は必ずステップ順に進むため、前のステップより早い指定は指定時刻どおりには送れない */}
                    {isTimingBehindPrevious(steps, index) && (
                      <p className="text-xs text-amber-700">前のステップより早い指定です。実際には前のステップが送られた直後に送られます。</p>
                    )}
                    {step.condition && (
                      <div className="grid gap-1.5 rounded-xl border border-dashed border-accent/50 bg-accent-soft/40 p-3 text-xs">
                        {/* 図の線と同じ「〜の場合」で書き、満たす／満たさないの読み替えを不要にする */}
                        <p className="flex flex-wrap items-baseline gap-2">
                          <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 font-semibold text-green-800">{conditionCaseLabel(step.condition, tags, true)}</span>
                          <span className="text-ink">{index + 1 < steps.length ? 'このステップを送信し、次へ進みます' : 'このステップを送信して、配信は完了です'}</span>
                        </p>
                        <p className="flex flex-wrap items-baseline gap-2">
                          <span className="shrink-0 rounded-full bg-neutral-50 px-2 py-0.5 font-semibold text-muted">{conditionCaseLabel(step.condition, tags, false)}</span>
                          <span className="text-ink">{falseLabel}</span>
                        </p>
                      </div>
                    )}
                    {incoming.length > 0 && (
                      <p className="text-xs text-muted">ステップ{incoming.join('・')}の条件を満たさなかった友だちは、ここへ来ます</p>
                    )}
                    {reachTagLabel(step.onReachTagId, tags) && (
                      <p className="text-xs text-ink">{reachTagLabel(step.onReachTagId, tags)}</p>
                    )}
                    <div className="min-w-0 overflow-x-auto">
                      <MessagePreview step={index + 1} messages={step.messages} assets={assets.data ?? []} references={scenario?.assetReferences ?? []} />
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted">ステップがありません。</p>
          )}
        </div>
      </section>
    </>
  );
}
